import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material/styles';
import PageArea from '../../shared/PageArea';
import { PageTitleWithButtons } from '../../shared/layout/PageTitleWithButtons';
import { ListPageSkeleton } from './ListPageSkeleton';
import { InsightTileGroup } from '../../shared/tiles/InsightTileGroup';
import type { InsightTileGroupProps } from '../../shared/tiles/InsightTileGroup';
import { TableSection } from '../../shared/tables/TableSection';
import type { TableSectionProps } from '../../shared/tables/TableSection';

// List pages default to the four-tile layout; set `layout` explicitly to opt into another one.
type RowFourHeadline = Extract<InsightTileGroupProps, { layout: 'row-four' }>;
type OtherListHeadline = Exclude<InsightTileGroupProps, { layout: 'row-four' }>;
export type ListPageHeadline =
  | (Omit<RowFourHeadline, 'layout'> & { layout?: 'row-four' })
  | OtherListHeadline;

export interface ListPageTemplateProps<T = unknown> {
  title: string;
  subtitle?: string;
  backButton?: ReactNode;
  actions?: ReactNode;
  headlineContent?: ListPageHeadline;
  filterContent?: ReactNode;
  quickFilterContent?: ReactNode;
  advancedFilterContent?: ReactNode;
  advancedFilterActions?: ReactNode;
  /** List pages always render their data via the shared TableSection. Use `emptyContent`/`loading` on it for empty and unready states. */
  table: TableSectionProps<T>;
  children?: ReactNode;
  loading?: boolean;
  loadingLabel?: string;
  showFiltersInSkeleton?: boolean;
  showQuickFiltersInSkeleton?: boolean;
  showAdvancedFilterInSkeleton?: boolean;
  showTableInSkeleton?: boolean;
  sx?: SxProps<Theme>;
}

export const ListPageTemplate = <T,>({
  title,
  subtitle,
  backButton,
  actions,
  headlineContent,
  filterContent,
  quickFilterContent,
  advancedFilterContent,
  advancedFilterActions,
  table,
  children,
  loading = false,
  loadingLabel,
  showFiltersInSkeleton = Boolean(filterContent),
  showQuickFiltersInSkeleton = Boolean(quickFilterContent),
  showAdvancedFilterInSkeleton = Boolean(advancedFilterContent),
  showTableInSkeleton = true,
  sx,
}: ListPageTemplateProps<T>) => {
  const { t } = useTranslation('common');
  const resolvedHeadline = headlineContent && ({
    layout: 'row-four',
    ...headlineContent,
  } as InsightTileGroupProps);

  if (loading) {
    return (
      <PageArea>
        <ListPageSkeleton
          showHeadline={Boolean(headlineContent)}
          showFilters={showFiltersInSkeleton}
          showQuickFilters={showQuickFiltersInSkeleton}
          showAdvancedFilter={showAdvancedFilterInSkeleton}
          showTable={showTableInSkeleton}
          loadingLabel={loadingLabel}
          sx={sx}
        />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box sx={sx}>
        {backButton !== undefined && backButton !== null && (
          // mb: 7 = 20px on the custom spacing scale, matching the old literal `marginBottom: '20px'` back-button spacing.
          <Box sx={{ mb: 7 }}>
            {backButton}
          </Box>
        )}

        <PageTitleWithButtons
          title={title}
          subTitle={subtitle ?? ''}
        >
          {actions !== undefined && actions !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {actions}
            </Box>
          )}
        </PageTitleWithButtons>

        {resolvedHeadline && (
          <Box sx={{ mt: 6 }}>
            <InsightTileGroup {...resolvedHeadline} />
          </Box>
        )}

        {filterContent !== undefined && filterContent !== null && (
          <Box sx={{ mt: 6 }}>
            {filterContent}
          </Box>
        )}

        {quickFilterContent !== undefined && quickFilterContent !== null && (
          <Box sx={{ mt: 6 }}>
            {quickFilterContent}
          </Box>
        )}

        {advancedFilterContent !== undefined && advancedFilterContent !== null && (
          <Accordion
            variant="borderless"
            disableGutters
            sx={{ mt: 3 }}
          >
            <AccordionSummary>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  pr: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {t('advancedFilters', 'Advanced Filters')}
                  <ExpandMore />
                </Box>
                {advancedFilterActions !== undefined && advancedFilterActions !== null && (
                  <Box
                    onClick={(event) => event.stopPropagation()}
                  >
                    {advancedFilterActions}
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {advancedFilterContent}
            </AccordionDetails>
          </Accordion>
        )}

        <Box sx={{ mt: 6 }}>
          <TableSection {...table} />
        </Box>

        {children !== undefined && children !== null && (
          <Box sx={{ mt: 4 }}>
            {children}
          </Box>
        )}
      </Box>
    </PageArea>
  );
};
